import { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { EnhancedDataTable } from "@redotech/redo-web/components/ui/enhanced-data-table";
import { tableNavigationShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import type { Row } from "@tanstack/react-table";
import { memo, useCallback, useMemo } from "react";
import { useSupportTicketsTableContext } from "../support-tickets-table-context";

export const SupportTicketsTable = memo(function SupportTicketsTable() {
  const {
    data,
    columns,
    isLoading,
    focusedSupportTicketId,
    setFocusedSupportTicketId,
    openSupportTicket,
    navigateUp,
    navigateDown,
    setActionMenuOpenForId,
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    setHoveredSupportTicketId,
  } = useSupportTicketsTableContext();

  const openActionMenuForFocused = useCallback(() => {
    if (focusedSupportTicketId) {
      setActionMenuOpenForId(focusedSupportTicketId);
    }
  }, [focusedSupportTicketId, setActionMenuOpenForId]);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        { shortcut: tableNavigationShortcuts.NAVIGATE_UP, handler: navigateUp },
        {
          shortcut: tableNavigationShortcuts.NAVIGATE_DOWN,
          handler: navigateDown,
        },
        {
          shortcut: tableNavigationShortcuts.OPEN_SUPPORT_TICKET,
          handler: openSupportTicket,
        },
        {
          shortcut: tableNavigationShortcuts.OPEN_ACTION_MENU,
          handler: openActionMenuForFocused,
        },
      ],
      [navigateUp, navigateDown, openSupportTicket, openActionMenuForFocused],
    ),
    scope: "supportTicketsTable",
  });

  const getRowId = useCallback(
    (row: SupportTicketListItemResponse) => row.id,
    [],
  );

  const handleFocusedRowChange = useCallback(
    (rowId: string | null) => {
      setFocusedSupportTicketId(rowId ? SupportTicketId.make(rowId) : null);
    },
    [setFocusedSupportTicketId],
  );

  const handleRowClick = useCallback(
    ({ row }: { row: SupportTicketListItemResponse }) => {
      openSupportTicket(row.id);
    },
    [openSupportTicket],
  );

  const getRowProps = useCallback(
    (row: Row<SupportTicketListItemResponse>) => ({
      onMouseEnter: () =>
        setHoveredSupportTicketId(SupportTicketId.make(row.id)),
      onMouseLeave: () => setHoveredSupportTicketId(null),
    }),
    [setHoveredSupportTicketId],
  );

  return (
    <EnhancedDataTable<SupportTicketListItemResponse>
      className="w-full"
      columnOrder={columnOrder}
      columns={columns}
      columnSizing={columnSizing}
      data={data}
      enableColumnReordering
      enableColumnResizing
      enableHorizontalScroll
      focusedRowId={focusedSupportTicketId}
      getRowId={getRowId}
      getRowProps={getRowProps}
      loading={isLoading}
      maxHeight="100%"
      onColumnOrderChange={setColumnOrder}
      onColumnSizingChange={setColumnSizing}
      onFocusedRowChange={handleFocusedRowChange}
      onRowClick={handleRowClick}
    />
  );
});
